"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Container } from "@/components/ui/Container";
import { TrustStars } from "@/components/TrustStars";
import { GoogleStars } from "@/components/GoogleStars";
import { GoogleLogo } from "@/components/GoogleLogo";
import { useExperimentVariant } from "@/hooks/useExperimentVariant";
import { site } from "@/lib/site";
import { cn } from "@/lib/utils";

type Review = { name: string; date: string; text: string; source?: Source };

// card width (280) + gap (20px = gap-5)
const STEP = 300;
const MAX_DOTS = 5;

// Laurel branch (from AirHelp's leaves.svg), inlined so it always renders.
// Colour comes from `text-*` in className (defaults to white for the blue card).
function Leaves({
  flip = false,
  className,
}: {
  flip?: boolean;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 23 58"
      fill="currentColor"
      aria-hidden="true"
      className={cn(
        "h-10 w-auto text-white",
        flip && "-scale-x-100",
        className,
      )}
    >
      <path d="M22.76 57.33c-.18-.16-17.78-17.01-10.3-44.14a.35.35 0 0 0-.24-.44c-.3-.08-.6.1-.69.4C4.04 40.7 21.95 57.83 22.13 58l.63-.67Z" />
      <path d="M3.48 41.73c2.4 7.49 9.44 5.7 9.44 5.7-2-7.34-9.44-5.7-9.44-5.7ZM9.79 38.6C.86 39.58.7 31.42.7 31.42c8.57-.16 9.08 7.18 9.08 7.18ZM.67 19.89c.93 10.3 7.98 9.2 7.98 9.2.58-9.6-7.98-9.2-7.98-9.2ZM5.4 5.13c-4.67 9.9 3.28 13.39 3.28 13.39C12.32 9.8 5.4 5.13 5.4 5.13ZM15.45 0c-7.7 4.15-3.13 11.82-3.13 11.82C20.58 4.72 15.45 0 15.45 0ZM22.8 37.01c-8.54 1.94-7.01 9.33-7.01 9.33C24.85 44.63 22.8 37 22.8 37ZM20.84 27.24c-8.26 2.46-7.7 10.1-7.7 10.1 8.21.21 7.7-10.1 7.7-10.1ZM20.48 19.24c-8.61.36-8.4 9.18-8.4 9.18 5.53.92 8.4-9.18 8.4-9.18ZM20.48 10.26c-7.84 1.28-7.59 8.16-7.59 8.16 7.5-.5 7.6-8.16 7.6-8.16Z" />
    </svg>
  );
}

function ReviewCard({
  review,
  google = false,
}: {
  review: Review;
  google?: boolean;
}) {
  const source: Source = review.source ?? "google";
  return (
    <figure className="flex w-[280px] shrink-0 snap-start flex-col rounded-xl bg-mist p-5">
      {google ? <GoogleStars size={20} /> : <TrustStars size={20} />}
      <blockquote className="mt-4 flex-1 text-[15px] font-medium leading-snug text-navy">
        {review.text}
      </blockquote>
      <figcaption className="mt-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-navy">
            {review.name}
          </p>
          <p className="mt-0.5 text-xs text-muted">{review.date}</p>
        </div>
        {/* Which platform the review came from, so the row can mix sources. */}
        <span
          role="img"
          aria-label={source === "google" ? "Google" : "Trustpilot"}
          className="grid size-7 shrink-0 place-items-center rounded-full bg-white shadow-sm"
        >
          <SourceMark source={source} className="size-4" />
        </span>
      </figcaption>
    </figure>
  );
}

type Source = "google" | "trustpilot";

const SOURCES: Source[] = ["google", "trustpilot"];

// Everything that differs between the two review sources the card can show.
const SOURCE_META = {
  google: {
    label: "googleLabel",
    caption: "ratingCardGoogle",
    rating: site.rating,
    url: site.reviewsUrl,
    accent: "#1a73e8",
  },
  trustpilot: {
    label: "trustpilotLabel",
    caption: "ratingCardTrustpilot",
    rating: site.trustpilotRating,
    url: site.trustpilotUrl,
    accent: "#00b67a",
  },
} as const;

// The review platform's icon-only mark (the notched star), taken from the same
// official artwork as the wordmark below. Official colours, so do not restyle.
function StarMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 271.3 258" aria-hidden="true" className={className}>
      <path
        fill="#00b67a"
        d="M271.3 98.6H167.7L135.7 0l-32.1 98.6L0 98.5l83.9 61L51.8 258l83.9-60.9 83.8 60.9-32-98.5 83.8-60.9z"
      />
      <path fill="#005128" d="M194.7 181.8l-7.2-22.3-51.8 37.6z" />
    </svg>
  );
}

// The review platform's own wordmark (star + type), inlined so it needs no
// external asset. Official colours, so do not restyle it.
function SourceWordmark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 1132.8 278.2" aria-hidden="true" className={className}>
      <path
        fill="#191919"
        d="M297.7 98.6h114.7V120h-45.1v120.3h-24.8V120h-44.9V98.6zm109.8 39.1h21.2v19.8h.4c.7-2.8 2-5.5 3.9-8.1 1.9-2.6 4.2-5.1 6.9-7.2 2.7-2.2 5.7-3.9 9-5.3 3.3-1.3 6.7-2 10.1-2 2.6 0 4.5.1 5.5.2s2 .3 3.1.4v21.8c-1.6-.3-3.2-.5-4.9-.7-1.7-.2-3.3-.3-4.9-.3-3.8 0-7.4.8-10.8 2.3-3.4 1.5-6.3 3.8-8.8 6.7-2.5 3-4.5 6.6-6 11s-2.2 9.4-2.2 15.1v48.8h-22.6V137.7zm164 102.6h-22.2V226h-.4c-2.8 5.2-6.9 9.3-12.4 12.4-5.5 3.1-11.1 4.7-16.8 4.7-13.5 0-23.3-3.3-29.3-10s-9-16.8-9-30.3v-65.1H504v62.9c0 9 1.7 15.4 5.2 19.1 3.4 3.7 8.3 5.6 14.5 5.6 4.8 0 8.7-.7 11.9-2.2 3.2-1.5 5.8-3.4 7.7-5.9 2-2.4 3.4-5.4 4.3-8.8.9-3.4 1.3-7.1 1.3-11.1v-59.5h22.6v102.5zm38.5-32.9c.7 6.6 3.2 11.2 7.5 13.9 4.4 2.6 9.6 4 15.7 4 2.1 0 4.5-.2 7.2-.5s5.3-1 7.6-1.9c2.4-.9 4.3-2.3 5.9-4.1 1.5-1.8 2.2-4.1 2.1-7-.1-2.9-1.2-5.3-3.2-7.1-2-1.9-4.5-3.3-7.6-4.5-3.1-1.1-6.6-2.1-10.6-2.9-4-.8-8-1.7-12.1-2.6-4.2-.9-8.3-2.1-12.2-3.4-3.9-1.3-7.4-3.1-10.5-5.4-3.1-2.2-5.6-5.1-7.4-8.6-1.9-3.5-2.8-7.8-2.8-13 0-5.6 1.4-10.2 4.1-14 2.7-3.8 6.2-6.8 10.3-9.1 4.2-2.3 8.8-3.9 13.9-4.9 5.1-.9 10-1.4 14.6-1.4 5.3 0 10.4.6 15.2 1.7 4.8 1.1 9.2 2.9 13.1 5.5 3.9 2.5 7.1 5.8 9.7 9.8 2.6 4 4.2 8.9 4.9 14.6h-23.6c-1.1-5.4-3.5-9.1-7.4-10.9-3.9-1.9-8.4-2.8-13.4-2.8-1.6 0-3.5.1-5.7.4-2.2.3-4.2.8-6.2 1.5-1.9.7-3.5 1.8-4.9 3.2-1.3 1.4-2 3.2-2 5.5 0 2.8 1 5 2.9 6.7 1.9 1.7 4.4 3.1 7.5 4.3 3.1 1.1 6.6 2.1 10.6 2.9 4 .8 8.1 1.7 12.3 2.6 4.1.9 8.1 2.1 12.1 3.4 4 1.3 7.5 3.1 10.6 5.4 3.1 2.3 5.6 5.1 7.5 8.5 1.9 3.4 2.9 7.7 2.9 12.7 0 6.1-1.4 11.2-4.2 15.5-2.8 4.2-6.4 7.7-10.8 10.3-4.4 2.6-9.4 4.6-14.8 5.8-5.4 1.2-10.8 1.8-16.1 1.8-6.5 0-12.5-.7-18-2.2-5.5-1.5-10.3-3.7-14.3-6.6-4-3-7.2-6.7-9.5-11.1-2.3-4.4-3.5-9.7-3.7-15.8H610zm74.6-69.7h17.1v-30.8h22.6v30.8h20.4v16.9h-20.4v54.8c0 2.4.1 4.4.3 6.2.2 1.7.7 3.2 1.4 4.4.7 1.2 1.8 2.1 3.3 2.7 1.5.6 3.4.9 6 .9 1.6 0 3.2 0 4.8-.1 1.6-.1 3.2-.3 4.8-.7v17.5c-2.5.3-5 .5-7.3.8-2.4.3-4.8.4-7.3.4-6 0-10.8-.6-14.4-1.7-3.6-1.1-6.5-2.8-8.5-5-2.1-2.2-3.4-4.9-4.2-8.2-.7-3.3-1.2-7.1-1.3-11.3v-60.5h-17.1v-17.1zm76.1 0h21.4v13.9h.4c3.2-6 7.6-10.2 13.3-12.8 5.7-2.6 11.8-3.9 18.5-3.9 8.1 0 15.1 1.4 21.1 4.3 6 2.8 11 6.7 15 11.7 4 5 6.9 10.8 8.9 17.4 2 6.6 3 13.7 3 21.2 0 6.9-.9 13.6-2.7 20-1.8 6.5-4.5 12.2-8.1 17.2-3.6 5-8.2 8.9-13.8 11.9-5.6 3-12.1 4.5-19.7 4.5-3.3 0-6.6-.3-9.9-.9-3.3-.6-6.5-1.6-9.5-2.9-3-1.3-5.9-3-8.4-5.1-2.6-2.1-4.7-4.5-6.5-7.2h-.4v51.2h-22.6V137.7zm79 51.4c0-4.6-.6-9.1-1.8-13.5-1.2-4.4-3-8.2-5.4-11.6-2.4-3.4-5.4-6.1-8.9-8.1-3.6-2-7.7-3.1-12.3-3.1-9.5 0-16.7 3.3-21.5 9.9-4.8 6.6-7.2 15.4-7.2 26.4 0 5.2.6 10 1.9 14.4 1.3 4.4 3.1 8.2 5.7 11.4 2.5 3.2 5.5 5.7 9 7.5 3.5 1.9 7.6 2.8 12.2 2.8 5.2 0 9.5-1.1 13.1-3.2 3.6-2.1 6.5-4.9 8.8-8.2 2.3-3.4 4-7.2 5-11.5.9-4.3 1.4-8.7 1.4-13.2zm39.9-90.5h22.6V120h-22.6V98.6zm0 39.1h22.6v102.6h-22.6V137.7zm42.8-39.1H945v141.7h-22.6V98.6zm91.9 144.5c-8.2 0-15.5-1.4-21.9-4.1-6.4-2.7-11.8-6.5-16.3-11.2-4.4-4.8-7.8-10.5-10.1-17.1-2.3-6.6-3.5-13.9-3.5-21.8 0-7.8 1.2-15 3.5-21.6 2.3-6.6 5.7-12.3 10.1-17.1 4.4-4.8 9.9-8.5 16.3-11.2 6.4-2.7 13.7-4.1 21.9-4.1s15.5 1.4 21.9 4.1c6.4 2.7 11.8 6.5 16.3 11.2 4.4 4.8 7.8 10.5 10.1 17.1 2.3 6.6 3.5 13.8 3.5 21.6 0 7.9-1.2 15.2-3.5 21.8-2.3 6.6-5.7 12.3-10.1 17.1-4.4 4.8-9.9 8.5-16.3 11.2-6.4 2.7-13.7 4.1-21.9 4.1zm0-17.9c5 0 9.4-1.1 13.1-3.2 3.7-2.1 6.7-4.9 9.1-8.3 2.4-3.4 4.1-7.3 5.3-11.6 1.1-4.3 1.7-8.7 1.7-13.2 0-4.4-.6-8.7-1.7-13.1s-2.9-8.2-5.3-11.6c-2.4-3.4-5.4-6.1-9.1-8.2-3.7-2.1-8.1-3.2-13.1-3.2s-9.4 1.1-13.1 3.2c-3.7 2.1-6.7 4.9-9.1 8.2-2.4 3.4-4.1 7.2-5.3 11.6-1.1 4.4-1.7 8.7-1.7 13.1 0 4.5.6 8.9 1.7 13.2 1.1 4.3 2.9 8.2 5.3 11.6 2.4 3.4 5.4 6.2 9.1 8.3 3.7 2.2 8.1 3.2 13.1 3.2zm58.4-87.5h17.1v-30.8h22.6v30.8h20.4v16.9h-20.4v54.8c0 2.4.1 4.4.3 6.2.2 1.7.7 3.2 1.4 4.4.7 1.2 1.8 2.1 3.3 2.7 1.5.6 3.4.9 6 .9 1.6 0 3.2 0 4.8-.1 1.6-.1 3.2-.3 4.8-.7v17.5c-2.5.3-5 .5-7.3.8-2.4.3-4.8.4-7.3.4-6 0-10.8-.6-14.4-1.7-3.6-1.1-6.5-2.8-8.5-5-2.1-2.2-3.4-4.9-4.2-8.2-.7-3.3-1.2-7.1-1.3-11.3v-60.5h-17.1v-17.1z"
      />
      <path
        fill="#00b67a"
        d="M271.3 98.6H167.7L135.7 0l-32.1 98.6L0 98.5l83.9 61L51.8 258l83.9-60.9 83.8 60.9-32-98.5 83.8-60.9z"
      />
      <path fill="#005128" d="M194.7 181.8l-7.2-22.3-51.8 37.6z" />
    </svg>
  );
}

// Compact mark, for the badge on a single review card.
function SourceMark({
  source,
  className,
}: {
  source: Source;
  className?: string;
}) {
  return source === "google" ? (
    <GoogleLogo className={className} />
  ) : (
    <StarMark className={className} />
  );
}

// Full lockup, for the rating card's source switcher. Google pairs its "G" with
// a text label; the other platform ships a wordmark that carries its own name.
function SourceLogo({
  source,
  label,
  className,
}: {
  source: Source;
  label: string;
  className?: string;
}) {
  if (source === "trustpilot") {
    return (
      <span
        role="img"
        aria-label={label}
        className={cn("flex items-center", className)}
      >
        <SourceWordmark className="h-5 w-auto" />
      </span>
    );
  }
  return (
    <span className={cn("flex items-center gap-2", className)}>
      <GoogleLogo className="size-5" />
      <span className="text-sm">{label}</span>
    </span>
  );
}

function ReviewRatingCard({ className }: { className?: string }) {
  const t = useTranslations("SocialProof");
  const [source, setSource] = useState<Source>("google");
  const [open, setOpen] = useState(false);
  const meta = SOURCE_META[source];

  return (
    <div
      className={cn(
        "flex-col items-center justify-center rounded-xl border border-line bg-white p-6 text-center",
        className,
      )}
    >
      <div
        className="relative"
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
        }}
      >
        <button
          type="button"
          aria-label={t("switchSource")}
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 rounded-lg px-2 py-1 text-sm font-semibold text-navy transition hover:bg-mist"
        >
          <SourceLogo source={source} label={t(meta.label)} />
          <ChevronDown
            className={cn("size-4 text-muted transition", open && "rotate-180")}
          />
        </button>

        {open && (
          <>
            {/* Click-anywhere-else backdrop; cheaper than a document listener. */}
            <button
              type="button"
              tabIndex={-1}
              aria-hidden="true"
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-10 cursor-default"
            />
            <div
              role="menu"
              className="absolute left-1/2 top-full z-20 mt-1 w-44 -translate-x-1/2 overflow-hidden rounded-lg border border-line bg-white py-1 shadow-lg"
            >
              {SOURCES.map((key) => (
                <button
                  key={key}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setSource(key);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition hover:bg-mist",
                    key === source ? "font-semibold text-navy" : "text-muted",
                  )}
                >
                  <SourceLogo source={key} label={t(SOURCE_META[key].label)} />
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="mt-3 flex items-center justify-center gap-3">
        <Leaves className="text-navy" />
        <p className="text-4xl font-extrabold text-navy">
          {meta.rating.value.toString()}/5
        </p>
        <Leaves flip className="text-navy" />
      </div>
      <div className="mt-2">
        {source === "google" ? (
          <GoogleStars size={22} />
        ) : (
          <TrustStars size={22} count={Math.round(meta.rating.value)} />
        )}
      </div>
      <p className="mt-4 text-xs leading-relaxed text-muted">
        {t(meta.caption, { count: meta.rating.count })}
      </p>
      <a
        href={meta.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: meta.accent, textDecorationColor: `${meta.accent}4d` }}
        className="mt-4 text-xs font-semibold underline underline-offset-4 transition hover:[text-decoration-color:currentColor]"
      >
        {t("seeAll")}
      </a>
    </div>
  );
}

function RatingCard({ className }: { className?: string }) {
  const t = useTranslations("SocialProof");
  return (
    <div
      className={cn(
        "relative flex-col items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-bright via-brand to-brand-dark p-6 text-white",
        className,
      )}
    >
      <div className="pointer-events-none absolute -right-12 -top-12 size-36 rounded-full bg-white/15 blur-2xl" />
      <div className="relative flex items-center gap-3">
        <Leaves />
        <p className="text-4xl font-extrabold text-white">
          {site.rating.value.toString()}/5
        </p>
        <Leaves flip />
      </div>
      <div className="relative mt-3">
        <TrustStars size={22} />
      </div>
      <p className="relative mt-4 text-center text-xs leading-relaxed text-white/75">
        {t("ratingCard", {
          count: site.rating.count,
        })}
      </p>
      {/* "See all reviews" link hidden for now — restore when the reviews page is ready.
      <a
        href={site.reviewsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="relative mt-4 text-xs font-semibold text-white underline decoration-white/40 underline-offset-4 transition hover:decoration-white"
      >
        {t("seeAll")}
      </a>
      */}
    </div>
  );
}

export function TrustStrip() {
  const t = useTranslations("SocialProof");
  const tr = useTranslations("Reviews");
  const items = tr.raw("items") as Review[];

  // `reviews-google-snippet` presence experiment: the Google-reviews snippet
  // (Google logo + gold stars, with a dropdown to the Trustpilot score) is the
  // control/SEO-default; the "green" holdback variant swaps back to the old
  // Trustpilot-look card after the flag loads.
  const green = useExperimentVariant("reviews-google-snippet") === "green";
  const Rating = green ? RatingCard : ReviewRatingCard;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [pages, setPages] = useState(items.length);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setActive(Math.round(el.scrollLeft / STEP));
    setPages(Math.round((el.scrollWidth - el.clientWidth) / STEP) + 1);
  };

  useEffect(() => {
    onScroll();
    window.addEventListener("resize", onScroll);
    return () => window.removeEventListener("resize", onScroll);
  }, []);

  // Show at most MAX_DOTS, sliding along with the active page.
  const first = Math.max(0, Math.min(active - 2, pages - MAX_DOTS));
  const dots = Array.from(
    { length: Math.min(pages, MAX_DOTS) },
    (_, i) => first + i,
  );
  const move = (dir: number) => {
    scrollRef.current?.scrollBy({ left: dir * STEP, behavior: "smooth" });
  };
  const goTo = (i: number) => {
    scrollRef.current?.scrollTo({ left: i * STEP, behavior: "smooth" });
  };

  return (
    <section
      className="bg-white py-10 lg:py-12"
      data-track-section="social_proof"
    >
      <Container>
        <h2 className="text-center text-xl font-extrabold tracking-tight text-navy sm:text-2xl">
          {t.rich("title", {
            count: site.helped,
            b: (chunks) => <span className="text-brand">{chunks}</span>,
          })}
        </h2>
      </Container>

      <Container className="mt-8">
        <div className="flex gap-4 lg:gap-5">
          {/* Rating card - fixed sidebar on desktop */}
          <Rating className="hidden shrink-0 sm:flex sm:w-[260px]" />

          {/* Scrollable review carousel. On mobile it bleeds past the container's
              right padding (-mr-5) so the next review peeks off the screen edge,
              signalling that the row is swipeable. */}
          <div
            ref={scrollRef}
            onScroll={onScroll}
            className="no-scrollbar -mr-5 flex flex-1 snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth sm:mr-0 lg:gap-5"
          >
            {/* On mobile the rating card rides along as the first carousel item */}
            <Rating className="flex w-[280px] shrink-0 snap-start sm:hidden" />
            {items.map((review, i) => (
              <ReviewCard
                key={`${review.name}-${i}`}
                review={review}
                google={!green}
              />
            ))}
          </div>
        </div>

        {/* Controls */}
        <div className="mt-7 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={() => move(-1)}
            aria-label="Vorige"
            className="grid size-9 shrink-0 place-items-center rounded-full border border-line text-navy transition hover:bg-mist"
          >
            <ChevronLeft className="size-5" />
          </button>

          <div className="flex items-center">
            {dots.map((i) => {
              const edge =
                (i === first && first > 0) ||
                (i === first + MAX_DOTS - 1 && i < pages - 1);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => goTo(i)}
                  aria-label={`Review ${i + 1}`}
                  aria-current={i === active}
                  className="grid size-6 place-items-center"
                >
                  <span
                    className={cn(
                      "rounded-full transition-all",
                      i === active
                        ? "h-2 w-5 bg-brand"
                        : edge
                          ? "size-1.5 bg-line"
                          : "size-2 bg-line",
                    )}
                  />
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => move(1)}
            aria-label="Volgende"
            className="grid size-9 shrink-0 place-items-center rounded-full border border-line text-navy transition hover:bg-mist"
          >
            <ChevronRight className="size-5" />
          </button>
        </div>
      </Container>
    </section>
  );
}
